import type { BasePaginationDTO, ListPaginatedDTO } from '@pct-sdk/core';
import { assertIsNotEmptyArray, assertMustContainUniqueArrayElements } from '@pct-ts/assert';
import { catchError, forkJoin, type Observable, of, switchMap, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import type { Constructor } from '@pct-ts/typing';
import { AbstractDtoService } from './abstract-dto.service';
import type { DefaultSort } from './default-sort';
import type { GetAllRequestParameters } from './get-all-request-parameters';
import type { HttpContext } from '@angular/common/http';

export interface RelatedDataConfig<RELATED_KEY extends string> {
  /** The key identifier for this related data type */
  key: RELATED_KEY;
  /** The service to use for loading this related data */
  service: AbstractDtoService<any, any, any, any, any>;
  /** Function to extract IDs from the main DTO for this related data */
  extractIds: (dto: any) => (number | string)[];
  /** Function to check if this related data should be loaded based on the DTOs */
  shouldLoad: (dtos: any[]) => boolean;
}

/**
 * Generic abstract service for DTOs with related DTOs
 */
export abstract class AbstractDtoWithRelatedDtoService<
  // A delegált szolgáltatás típusa, amelyet a DI konténerből injektálunk
  DELEGATED_SERVICE,
  // Az eredeti DTO szolgáltatás típusa, amely az AbstractDtoService-ből származik
  ORIGINAL_DTO_SERVICE extends AbstractDtoService<any, any, any, any, any>,
  // A létrehozás és módosítás műveletekhez használt DTO típusa
  CREATE_UPDATE_DTO,
  // A fő DTO típusa, amely az alap adatokat tartalmazza
  MAIN_DTO,
  // A kapcsolódó adatokkal bővített GET DTO típusa, amelyet visszaadunk
  RELATED_GET_DTO,
  // A kapcsolódó adatokkal bővített lista DTO típusa
  RELATED_LIST_DTO,
  // A fő DTO azonosító mezőjének kulcsa, amely mindkét DTO típusban megtalálható
  MAIN_DTO_ID_KEY extends keyof MAIN_DTO & keyof RELATED_GET_DTO,
  // A kapcsolódó adatok kulcsainak string literál típusa (pl. 'CATEGORY' | 'LOCATION')
  RELATED_KEY extends string,
  // A fő DTO azonosító mezőjének típusa, alapértelmezetten a MAIN_DTO megfelelő mezőjéből származik
  MAIN_DTO_ID_TYPE = MAIN_DTO[MAIN_DTO_ID_KEY],
  // A getAll kérések paramétereinek típusa, alapértelmezetten ids és searchText mezőkkel
  GET_ALL_PARAMETERS extends {
    ids?: number[] | string[] | undefined;
    searchText?: string | undefined;
  } = any,
  // A lapozás információinak DTO típusa, alapértelmezetten BasePaginationDTO-ból származik
  PAGINATED_DTO extends BasePaginationDTO = any,
> extends AbstractDtoService<
  DELEGATED_SERVICE,
  CREATE_UPDATE_DTO,
  RELATED_GET_DTO,
  RELATED_LIST_DTO,
  MAIN_DTO_ID_KEY,
  GET_ALL_PARAMETERS,
  PAGINATED_DTO,
  MAIN_DTO_ID_TYPE
> {
  protected abstract readonly relatedDataConfigs: RelatedDataConfig<RELATED_KEY>[];
  protected readonly loadRelatedDataKeys: RELATED_KEY[];
  protected readonly originalDtoService: ORIGINAL_DTO_SERVICE;

  protected constructor(
    defaultSort: DefaultSort<RELATED_LIST_DTO>,
    delegatedServiceType: Constructor<DELEGATED_SERVICE>,
    originalDtoServiceType: Constructor<ORIGINAL_DTO_SERVICE>,
    loadRelatedDataKeys: RELATED_KEY[],
  ) {
    super(defaultSort, delegatedServiceType);

    assertIsNotEmptyArray(
      loadRelatedDataKeys,
      'Load related data keys must be provided for DTO service with related data',
    );
    assertMustContainUniqueArrayElements(
      loadRelatedDataKeys,
      'Load related data keys must contain unique elements',
    );

    this.loadRelatedDataKeys = loadRelatedDataKeys;
    this.originalDtoService = this.injectService(originalDtoServiceType);
  }

  override create(dto: CREATE_UPDATE_DTO, httpContext: HttpContext): Observable<RELATED_GET_DTO> {
    return this.originalDtoService
      .create(dto, httpContext)
      .pipe(
        switchMap(response =>
          this.getById(this.getDtoId(response) as MAIN_DTO_ID_TYPE, httpContext),
        ),
      );
  }
  override update(
    id: MAIN_DTO_ID_TYPE,
    dto: CREATE_UPDATE_DTO,
    httpContext: HttpContext,
  ): Observable<RELATED_GET_DTO> {
    return this.originalDtoService
      .update(id, dto, httpContext)
      .pipe(
        switchMap(response =>
          this.getById(this.getDtoId(response) as MAIN_DTO_ID_TYPE, httpContext),
        ),
      );
  }

  override delete(id: MAIN_DTO_ID_TYPE, httpContext: HttpContext): Observable<void> {
    return this.originalDtoService.delete(id, httpContext);
  }

  override getById(id: MAIN_DTO_ID_TYPE, httpContext?: HttpContext): Observable<RELATED_GET_DTO> {
    return this.originalDtoService
      .getById(id, httpContext)
      .pipe(
        switchMap(response =>
          this.loadRelatedData([response], httpContext).pipe(
            map(relatedData => this.mapDtoWithRelatedData(response, relatedData)),
          ),
        ),
      );
  }

  override getByIds(
    ids: MAIN_DTO_ID_TYPE[],
    httpContext?: HttpContext,
  ): Observable<RELATED_GET_DTO[]> {
    return this.originalDtoService.getByIds(ids, httpContext).pipe(
      switchMap(response => {
        if (response.length === 0) {
          return of([]);
        }

        return this.loadRelatedData(response, httpContext).pipe(
          map(relatedData => response.map(dto => this.mapDtoWithRelatedData(dto, relatedData))),
        );
      }),
    );
  }

  override getAll(
    parameters: GetAllRequestParameters<GET_ALL_PARAMETERS>,
    httpContext?: HttpContext,
  ): Observable<ListPaginatedDTO<RELATED_LIST_DTO, PAGINATED_DTO>> {
    return this.originalDtoService.getAll(parameters, httpContext).pipe(
      switchMap(response => {
        if (!Array.isArray(response.data) || response.data.length === 0) {
          return of({ ...response, data: null });
        }

        return this.loadRelatedData(response.data, httpContext).pipe(
          map(relatedData => ({
            ...response,
            data: response.data!.map(dto => this.mapDtoWithRelatedData(dto, relatedData)),
          })),
        );
      }),
    ) as Observable<ListPaginatedDTO<RELATED_LIST_DTO, PAGINATED_DTO>>;
  }

  override getDtoId(dto: RELATED_LIST_DTO): MAIN_DTO_ID_TYPE;
  override getDtoId(dto: RELATED_GET_DTO): MAIN_DTO_ID_TYPE;
  override getDtoId(dto: RELATED_GET_DTO | null | undefined): MAIN_DTO_ID_TYPE | undefined | null;
  override getDtoId(
    dto: Partial<RELATED_GET_DTO> | null | undefined,
  ): MAIN_DTO_ID_TYPE | undefined | null;
  override getDtoId(
    dto: RELATED_LIST_DTO | RELATED_GET_DTO | Partial<RELATED_GET_DTO> | undefined | null,
  ): MAIN_DTO_ID_TYPE | undefined | null {
    return this.originalDtoService.getDtoId(dto as any);
  }

  override toString(dto: RELATED_GET_DTO): string {
    return this.originalDtoService.toString(dto as any);
  }

  protected loadRelatedData(
    dtos: MAIN_DTO[],
    httpContext?: HttpContext,
  ): Observable<Record<RELATED_KEY, any[]>> {
    const observables: Record<string, Observable<ListPaginatedDTO<any>>> = {};

    for (const config of this.relatedDataConfigs) {
      if (this.loadRelatedDataKeys.includes(config.key) && config.shouldLoad(dtos)) {
        const allIds = [...new Set(dtos.flatMap(dto => config.extractIds(dto)))];
        if (allIds.length > 0) {
          observables[config.key] = config.service
            .getAll(
              {
                // TODO: is it needed?
                // pagination: parameters?.pagination,
                // parameters: { ...(parameters?.parameters ?? {}), ids: allIds },
                parameters: { ids: allIds },
              },
              httpContext,
            )
            .pipe(
              catchError(error => {
                if (error.status === 403) {
                  return of({ data: [], pagination: { totalCount: 0 } } as ListPaginatedDTO<any>);
                }
                return throwError(() => error);
              }),
            );
        }
      }
    }

    if (Object.keys(observables).length === 0) {
      return of({} as Record<RELATED_KEY, any[]>);
    }
    return forkJoin(observables).pipe(
      map(responses =>
        Object.entries(responses).reduce(
          (acc, next) => {
            acc[next[0] as RELATED_KEY] = next[1].data ?? [];
            return acc;
          },
          {} as Record<RELATED_KEY, any[]>,
        ),
      ),
    );
  }

  protected abstract mapDtoWithRelatedData(
    dto: MAIN_DTO,
    relatedData: Record<RELATED_KEY, any[]>,
  ): RELATED_GET_DTO;
}
